// THE THREAD — innernet's one line (law_cell_thread).
//
// Wherever innernet joins one thing to another (netti to an agent, an agent to
// its sub-agents, the deck's apps round netti, the landing's constellation) the
// line is the brand CELL stamped along a path: small cells at random size and
// rotation, a breath apart, a hair off the centre line, under one soft glow.
// Never an SVG stroke, never a dashed border, never a gradient bar.
//
// This module is the brush and the geometry, with no DOM in it, so every
// renderer stamps the same thread and a test can pin it. The brush was measured
// off the Figma hero (Wheel.tsx and landing/test1/ThreadFig.tsx carry the same
// numbers and are where it was first drawn).
//
// Performance, the reason it is shaped this way:
//   · the stamps raster ONCE into a canvas when the geometry changes, never per
//     frame. Motion is a CSS transform on that canvas (an orbit turning is the
//     layer rotating), so the GPU moves a cached texture;
//   · the glow is the same bitmap copied to a second canvas under ONE css blur,
//     never `shadowBlur` per stamp;
//   · one matrix write per stamp (setTransform), no save/restore;
//   · the device ratio is capped at 1.25: a field of 1–5px cells under a blur
//     shows no difference at 2x for four times the fill.

import { CELL } from './cell'

/** the measured brush: px between stamps, jitter of that gap, how far off the
 *  centre line a stamp may sit, the size range and how strongly it leans small */
export const THREAD_BRUSH = { gap: 6.2, jit: 0.55, band: 0.95, sMin: 1.3, sMax: 5.2, bias: 1.75 } as const

export type Brush = { gap: number; jit: number; band: number; sMin: number; sMax: number; bias: number }
export type Pt = readonly [number, number]

/** a thread to stamp: its points (a polyline, already in px) and how present it is */
export interface Thread {
  pts: readonly Pt[]
  /** 0..1: scales the cells' size and opacity; a quiet thread is thinner, not fainter only */
  weight?: number
  /** a stable seed, so the same thread is stamped the same way every draw */
  seed?: number
}

/** the cell's own frame: a stamp is scaled from its native width, about its centre */
const CELL_W = CELL.width
const CELL_CX = CELL.width / 2
const CELL_CY = CELL.height / 2

/** mulberry32: a small, fast, seeded generator, so a thread never shimmers between draws */
export function rng32(seed: number): () => number {
  let a = seed | 0
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** a seed from a string (an id), the same on the server and in every browser */
export function seedOf(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

/** points along a circular arc, from angle a0 to a1 (radians, either direction) */
export function arcPoints(cx: number, cy: number, r: number, a0: number, a1: number, step = 8): Pt[] {
  const n = Math.max(2, Math.ceil((Math.abs(a1 - a0) * r) / step))
  const out: Pt[] = []
  for (let i = 0; i <= n; i++) {
    const a = a0 + ((a1 - a0) * i) / n
    out.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r])
  }
  return out
}

/** points along a quadratic curve p0 → p1 bowed through control c */
export function curvePoints(p0: Pt, c: Pt, p1: Pt, step = 6): Pt[] {
  const approx = Math.hypot(c[0] - p0[0], c[1] - p0[1]) + Math.hypot(p1[0] - c[0], p1[1] - c[1])
  const n = Math.max(2, Math.ceil(approx / step))
  const out: Pt[] = []
  for (let i = 0; i <= n; i++) {
    const t = i / n
    const u = 1 - t
    out.push([u * u * p0[0] + 2 * u * t * c[0] + t * t * p1[0], u * u * p0[1] + 2 * u * t * c[1] + t * t * p1[1]])
  }
  return out
}

/**
 * The control point that bows a thread between two points: off the chord's
 * middle by `bow` of its length, to the left of travel (negative bows right).
 * A thread is never ruler-straight; a slight bow is what makes it read as grown.
 */
export function bowOf(p0: Pt, p1: Pt, bow = 0.12): Pt {
  const mx = (p0[0] + p1[0]) / 2
  const my = (p0[1] + p1[1]) / 2
  const dx = p1[0] - p0[0]
  const dy = p1[1] - p0[1]
  return [mx - dy * bow, my + dx * bow]
}

/** one stamp, worked out: where, how big, turned how far, how opaque */
export interface Stamp {
  x: number
  y: number
  size: number
  angle: number
  alpha: number
}

/**
 * Walk a polyline and lay the stamps along it. Pure: the same thread and seed
 * give the same stamps, so the canvas, a test and a server render agree.
 */
export function stampsOf(thread: Thread, brush: Brush = THREAD_BRUSH): Stamp[] {
  const w = Math.max(0.15, Math.min(1, thread.weight ?? 1))
  const rnd = rng32(thread.seed ?? 1)
  const out: Stamp[] = []
  const pts = thread.pts
  // carry the walk across segments so a thread drawn as many short pieces is
  // spaced exactly like one drawn as a single long one
  let carry = brush.gap * rnd()
  for (let i = 1; i < pts.length; i++) {
    const [x1, y1] = pts[i - 1]
    const [x2, y2] = pts[i]
    const len = Math.hypot(x2 - x1, y2 - y1)
    if (len < 0.01) continue
    const ux = (x2 - x1) / len
    const uy = (y2 - y1) / len
    let dist = carry
    while (dist < len) {
      const size = (brush.sMin + Math.pow(rnd(), brush.bias) * (brush.sMax - brush.sMin)) * (0.55 + 0.45 * w)
      const off = (rnd() - 0.5) * 2 * brush.band
      out.push({
        x: x1 + ux * dist - uy * off,
        y: y1 + uy * dist + ux * off,
        size,
        angle: rnd() * Math.PI * 2,
        alpha: (0.5 + rnd() * 0.5) * (0.45 + 0.55 * w),
      })
      dist += brush.gap * (1 - brush.jit + rnd() * brush.jit * 2)
    }
    carry = dist - len
  }
  return out
}

let cellPath: Path2D | null = null
function cell(): Path2D | null {
  if (cellPath) return cellPath
  if (typeof Path2D === 'undefined') return null
  cellPath = new Path2D(CELL.path)
  return cellPath
}

/** the device ratio a thread canvas is drawn at (see the header) */
export function threadDpr(): number {
  return typeof window === 'undefined' ? 1 : Math.min(1.25, window.devicePixelRatio || 1)
}

/**
 * Stamp threads into a 2d context whose backing store is `dpr` times its css
 * size. The fill is whatever `ctx.fillStyle` already is: the caller picks the
 * ink (read from css, so a thread follows the ground and the organism's hue).
 * Returns how many cells were stamped.
 */
export function paintThreads(ctx: CanvasRenderingContext2D, threads: readonly Thread[], dpr: number, brush: Brush = THREAD_BRUSH): number {
  const path = cell()
  if (!path) return 0
  let n = 0
  for (const t of threads) {
    for (const s of stampsOf(t, brush)) {
      const k = (s.size / CELL_W) * dpr
      const cos = Math.cos(s.angle) * k
      const sin = Math.sin(s.angle) * k
      ctx.setTransform(cos, sin, -sin, cos, s.x * dpr, s.y * dpr)
      ctx.translate(-CELL_CX, -CELL_CY)
      ctx.globalAlpha = s.alpha
      ctx.fill(path)
      n++
    }
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.globalAlpha = 1
  return n
}
